import { DownloadOutlined } from '@ant-design/icons';
import { DatePicker, Select, SelectProps } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import moment from 'moment';
import { FC, useEffect, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { IRestTotalReq } from '@api/services/rKeeperApi/types';
import { getTopPositions } from '@api/thunks/reports';
import { getRestaurants } from '@api/thunks/rKeeper';
import { Icons } from '@assets';
import { AppDispatch } from '@config/store';
import { useApplication } from '@/context/ApplicationContext';
import { authInfoSelect } from '@entities/auth';
import { reportsSelect, setFilteredTop } from '@entities/reports';
import { rKeeperSelect } from '@entities/rKeeper';
import { colors } from '@styles';
import { flexMixin, textMixin } from '@styles/mixins';
import { SelectOption } from '@types';
import { API_DATE_FORMAT, columnsCSV, DISPLAY_DATE_FORMAT } from '@utils/const';
import { isNotEmptyArray } from '@utils/functions';
import { getNoun } from '@utils/functions';
import { TableWrapper } from './components/reports/TableTopWrapper';
import { ColumnTopPositions } from './components/reports/ui/columnTopPositions';

const { RangePicker } = DatePicker;

const TopPositionsPage: FC = () => {
    const { dateRange, setDateRange } = useApplication();
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const { restaurants, selectedRestaurant } = useSelector(rKeeperSelect);
    const [fromDate, setFromDate] = useState<string | number>(
        moment().subtract(1, 'week').format(API_DATE_FORMAT)
    );
    const [toDate, setToDate] = useState<string | number>(
        moment().format(API_DATE_FORMAT)
    );
    const { shop } = useSelector(authInfoSelect);
    const {
        topPositions,
        categoriesNames,
        inventedCategories,
        filteredTopPositions,
        loading,
    } = useSelector(reportsSelect);
    const dispatch = useDispatch<AppDispatch>();

    const from = useMemo(() => {
        return dateRange && dateRange.from
            ? dateRange.from
            : moment()
                  .subtract(1, 'month')
                  .utc()
                  .format(API_DATE_FORMAT)
                  .valueOf();
    }, [dateRange]);
    const to = useMemo(
        () =>
            dateRange && dateRange.to
                ? dateRange.to
                : moment().utc().format(API_DATE_FORMAT).valueOf().valueOf(),
        [dateRange]
    );

    const options: SelectProps['options'] = [
        { label: 'Выбрать все', value: '0' },
        ...(restaurants?.map(item => ({
            label: item.name,
            value: item.id.toString(),
        })) || []),
    ];

    const handleChange = (values: string[]) => {
        if (values.includes('0')) {
            if (!selectedValues.includes('0')) {
                setSelectedValues([
                    ...options
                        .filter((opt): opt is SelectOption => !!opt.value)
                        .map(opt => opt.value),
                ]);
            } else {
                setSelectedValues([]);
            }
        } else {
            setSelectedValues(values);
        }
    };

    const onDateChange = (date: any, dateStrings: [string, string]) => {
        if (date) {
            setDateRange({
                from: dayjs(dateStrings[0], DISPLAY_DATE_FORMAT)
                    .format(API_DATE_FORMAT)
                    .valueOf(),
                to: dayjs(dateStrings[1], DISPLAY_DATE_FORMAT)
                    .format(API_DATE_FORMAT)
                    .valueOf(),
            });
        }
    };

    useEffect(() => {
        if (shop) {
            dispatch(getRestaurants(shop.id));
        }
    }, [shop, dispatch]);

    useEffect(() => {
        if (isNotEmptyArray(selectedRestaurant)) {
            setSelectedValues([selectedRestaurant[0].id?.toString()]);
        }
    }, [selectedRestaurant]);

    useEffect(() => {
        if (shop) {
            const params: IRestTotalReq = {
                from: moment.utc(from).format(API_DATE_FORMAT),
                to: moment.utc(to).format(API_DATE_FORMAT),
            };
            if (selectedValues[0]) {
                params.restaurant = selectedValues.join(',');
            }
            setFromDate(from);
            setToDate(to);
            dispatch(getTopPositions({ shop_id: shop.id, params }));
        }
    }, [shop, dispatch, from, to, selectedValues]);

    const columns: ColumnsType = useMemo(
        () => ColumnTopPositions({ inventedCategories, categoriesNames }),
        [inventedCategories, categoriesNames]
    );

    return (
        <Wrapper>
            <div style={{ display: 'flex', gap: '20px' }}>
                <Title>
                    Категории
                    <SubTitle>
                        {categoriesNames
                            ? getNoun(
                                  categoriesNames.length,
                                  'категория',
                                  'категории',
                                  'категорий'
                              )
                            : 'нет товаров'}
                    </SubTitle>
                </Title>
                <Title>
                    Товары
                    <SubTitle>
                        {topPositions
                            ? getNoun(
                                  topPositions.length,
                                  'товар',
                                  'товара',
                                  'товаров'
                              )
                            : 'нет товаров'}
                    </SubTitle>
                </Title>
            </div>
            <Header>
                <WrapSelect>
                    <div>Выбор ресторана:</div>
                    <Select
                        mode="multiple"
                        allowClear
                        style={{ minWidth: '240px' }}
                        maxTagCount={7}
                        placeholder="Выбрать рестораны"
                        value={selectedValues}
                        onChange={handleChange}
                        options={options}
                    />
                </WrapSelect>

                <StyledCaledarWrapper>
                    <CSVLink
                        data={filteredTopPositions}
                        separator={';'}
                        headers={columnsCSV?.map(col => ({
                            label: col.title as string,
                            key: col.dataIndex as string,
                        }))}
                        filename={`toppositions_${moment
                            .utc(fromDate)
                            .format(API_DATE_FORMAT)}_${moment
                            .utc(toDate)
                            .format(API_DATE_FORMAT)}.csv`}
                    >
                        <StyledCalendarMemo>
                            <DownloadOutlined style={{ color: 'hotpink' }} />
                            Экспортировать в CSV
                        </StyledCalendarMemo>
                    </CSVLink>

                    <StyledRangePicker
                        suffixIcon={
                            <img
                                src={Icons.IconMainCalendar}
                                alt="icon calendar"
                            />
                        }
                        value={[
                            dayjs(fromDate, API_DATE_FORMAT),
                            dayjs(toDate, API_DATE_FORMAT),
                        ]}
                        format={DISPLAY_DATE_FORMAT}
                        onChange={onDateChange}
                    />
                </StyledCaledarWrapper>
            </Header>
            <TableWrapper
                data={topPositions}
                loading={loading === 'loading'}
                columns={columns}
                setFilteredData={data => dispatch(setFilteredTop(data))}
                filteredData={filteredTopPositions}
            />
        </Wrapper>
    );
};

export default TopPositionsPage;

const Wrapper = styled.div`
    min-width: 100%;
    background-color: ${colors.cleanWhite};
    padding: 16px 29px 16px 128px;
    .ant-table-thead > tr > th {
        background-color: ${colors.tableHeadBg};
        ${textMixin({
            fontSize: '14px',
            fontWeight: 400,
        })};
        line-height: 16.8px;
        text-align: left;
    }

    .ant-table-footer {
        background-color: ${colors.tableHeadBg};
        ${textMixin({
            fontSize: '14px',
            fontWeight: 600,
        })};
        line-height: 16.8px;
        text-align: left;
    }

    table > tbody > tr > td {
        background-color: ${colors.cleanWhite};
        border: 1px solid #efefef;
        color: ${colors.grey};
        ${textMixin({
            fontSize: '14px',
            fontWeight: 400,
        })};
        line-height: 16.8px;
        text-align: left;
        height: 30px;
    }
`;

const Header = styled.div`
    width: 100%;
    ${flexMixin('row', 'space-between', 'center')};
    flex-wrap: wrap-reverse;
    gap: 30px;
    margin: 20px 0;
`;

const WrapSelect = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    flex-wrap: wrap;
    flex: 1;
    .ant-select-selection-item-content {
        height: 24px;
        line-height: 22px;
    }
`;

const Title = styled.div`
    display: flex;
    flex-direction: column;
    ${textMixin({
        fontSize: '18px',
        fontWeight: 600,
    })};
    line-height: 26px;
    letter-spacing: 0.02em;
    color: ${colors.blackText};
`;

const SubTitle = styled.span`
    ${textMixin({
        fontSize: '13px',
        fontWeight: 500,
    })};
    line-height: 26px;
    letter-spacing: 0.02em;
    color: #999999;
`;

const StyledRangePicker = styled(RangePicker)`
    width: 250px;
    color: ${colors.cleanWhite};
`;

const StyledCaledarWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    color: ${colors.cleanWhite};

    & svg {
        width: 20px;
        height: 20px;
    }
    .ant-picker {
        background-color: ${colors.primaryBg};
        ${textMixin({
            fontSize: '14px',
            fontWeight: 400,
        })};
        line-height: 28px;
        text-align: center;
        color: ${colors.cleanWhite};
    }

    .ant-picker .ant-picker-input,
    .ant-picker .ant-picker-range,
    .ant-picker .ant-picker-input-active .ant-picker-input-placeholder {
        display: flex;
        flex-direction: row-reverse;
        ${textMixin({
            fontSize: '14px',
            fontWeight: 400,
        })};
        line-height: 28px;
        color: ${colors.cleanWhite};
    }

    .ant-picker .ant-picker-input .ant-picker-input-active {
        color: ${colors.cleanWhite} !important;
    }

    .ant-picker .ant-picker-input .ant-picker-input-placeholder {
        color: ${colors.cleanWhite} !important;
    }

    .ant-picker .ant-picker-input span.ant-picker-suffix,
    .ant-picker.ant-picker-range span.ant-picker-suffix {
        order: -1;
        margin-left: 0;
        margin-right: 10px;
        color: ${colors.cleanWhite};
    }

    .ant-picker.ant-picker-range.ant-picker-range-separator {
        color: ${colors.cleanWhite} !important;
    }

    .ant-picker-range-separator > span {
        color: ${colors.cleanWhite} !important;
    }

    .ant-picker-clear {
        color: ${colors.cleanWhite} !important;
    }

    .ant-picker .ant-picker-clear {
        position: absolute;
        right: 217px;
    }
`;

const StyledCalendarMemo = styled.div`
    width: 220px;
    height: 40px;
    gap: 10px;
    background-color: #e9e9e9;
    border-radius: 5px 0 0;
    opacity: 0;
    ${flexMixin('row', 'center', 'center')};
    ${textMixin({
        fontSize: '14px',
        fontWeight: 400,
    })};
    line-height: 28px;
    text-align: left;
    color: ${colors.grey};
`;
